import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProducts, createProduct, updateProduct, deleteProduct, exportProductsCsv, importProductsCsv, exportProductsExcel, bulkUpdateProducts } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableHeader from '../components/SortableHeader';
import ExportMenu from '../components/ExportMenu';
import { ColumnsButton } from '../components/ColumnConfigurator';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import { Plus, Search, Package, Edit, Trash2, Download, Upload, X, Check } from 'lucide-react';
import { fmtCurrency } from '../utils/formatCurrency';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import './AccountDetail/AccountDetail.css';

function Products() {
  const { canDo } = useAuth();
  const { visibleColumns } = useColumns('products');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importCsv, setImportCsv] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // Edit mode state — mirrors opportunity products tab pattern
  const [isEditing, setIsEditing] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const extraParams = useMemo(() => {
    const p = {};
    if (activeFilter) p.active = activeFilter;
    return p;
  }, [activeFilter]);

  const {
    data: products, loading, error, page, totalPages, total, limit,
    setPage, setLimit, sortBy, sortOrder, handleSort, search, setSearch, refetch
  } = usePagination(getProducts, {
    queryKey: 'products',
    initialLimit: 25,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    extraParams
  });

  const displayError = localError || error;

  // Edit mode helpers
  const startEditing = () => {
    setEditRows(products.map(p => ({ ...p })));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditRows([]);
  };

  const addEditRow = () => {
    if (!isEditing) {
      setEditRows([...products.map(p => ({ ...p })), { id: null, name: '', sku: '', description: '', price: 0, active: true, _isNew: true }]);
      setIsEditing(true);
    } else {
      setEditRows(prev => [...prev, { id: null, name: '', sku: '', description: '', price: 0, active: true, _isNew: true }]);
    }
  };

  const updateEditRow = (index, field, value) => {
    setEditRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const removeEditRow = (index) => {
    const row = editRows[index];
    if (row._isNew) {
      setEditRows(prev => prev.filter((_, i) => i !== index));
    } else {
      setDeleteConfirm(row.id);
    }
  };

  const saveEditing = async () => {
    setSaving(true);
    try {
      // Save existing rows that changed
      for (const row of editRows) {
        if (row._isNew) {
          if (row.name.trim()) {
            await createProduct({ name: row.name, sku: row.sku, description: row.description, price: parseFloat(row.price) || 0, active: row.active });
          }
        } else {
          const original = products.find(p => p.id === row.id);
          if (original && (
            original.name !== row.name ||
            original.sku !== row.sku ||
            original.description !== row.description ||
            original.price !== parseFloat(row.price) ||
            original.active !== row.active
          )) {
            await updateProduct(row.id, { ...row, price: parseFloat(row.price) || 0 });
          }
        }
      }
      setIsEditing(false);
      setEditRows([]);
      refetch();
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Failed to save products');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct(deleteConfirm);
      setDeleteConfirm(null);
      // Also remove from editRows if in edit mode
      if (isEditing) {
        setEditRows(prev => prev.filter(r => r.id !== deleteConfirm));
      }
      refetch();
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Failed to delete product');
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportProductsCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.csv';
      a.click();
    } catch (err) {
      setLocalError('Failed to export products');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    try {
      const result = await importProductsCsv(importCsv, updateExisting);
      setShowImportModal(false);
      setImportCsv('');
      alert(result.data.message);
      refetch();
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Failed to import products');
    }
  };

  // Read-only cell renderer
  const renderReadOnlyCell = (col, product) => {
    switch (col.key) {
      case 'name':
        return product.name;
      case 'sku':
        return product.sku || <EmptyValue />;
      case 'description':
        return product.description || <EmptyValue />;
      case 'price':
        return `$${fmtCurrency(product.price)}`;
      case 'active':
        return <span className={`badge ${product.active ? 'badge-success' : 'badge-gray'}`}>{product.active ? 'Active' : 'Inactive'}</span>;
      case 'created_at':
        return product.created_at ? new Date(product.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return product[col.key] || <EmptyValue />;
    }
  };

  // Edit mode cell renderer
  const renderEditCell = (col, row, index) => {
    switch (col.key) {
      case 'name':
        return (
          <input
            type="text"
            className="inline-table-input"
            value={row.name}
            onChange={(e) => updateEditRow(index, 'name', e.target.value)}
            placeholder="Product name"
          />
        );
      case 'sku':
        return (
          <input
            type="text"
            className="inline-table-input"
            value={row.sku || ''}
            onChange={(e) => updateEditRow(index, 'sku', e.target.value)}
            placeholder="SKU"
          />
        );
      case 'description':
        return (
          <input
            type="text"
            className="inline-table-input"
            value={row.description || ''}
            onChange={(e) => updateEditRow(index, 'description', e.target.value)}
            placeholder="Description"
          />
        );
      case 'price':
        return (
          <input
            type="number"
            step="0.01"
            className="inline-table-input"
            value={row.price}
            onChange={(e) => updateEditRow(index, 'price', e.target.value)}
            style={{ width: '110px' }}
          />
        );
      case 'active':
        return (
          <span
            className={`badge ${row.active ? 'badge-success' : 'badge-gray'} badge-toggle`}
            onClick={() => updateEditRow(index, 'active', !row.active)}
            role="button"
            title={row.active ? 'Click to deactivate' : 'Click to activate'}
            style={{ cursor: 'pointer' }}
          >
            {row.active ? 'Active' : 'Inactive'}
          </span>
        );
      case 'created_at':
        return row.created_at ? new Date(row.created_at).toLocaleDateString() : <EmptyValue label="—" />;
      default:
        return row[col.key] || <EmptyValue />;
    }
  };

  const getRowActions = (product) => [
    canDo('products', 'delete') && { key: 'delete', label: 'Delete', icon: Trash2, onClick: () => setDeleteConfirm(product.id), variant: 'danger' },
  ].filter(Boolean);

  return (
    <div className="products-page">
      <div className="page-header">
        <h1 className="page-title">Products</h1>
        <div className="page-actions">
          {!isEditing && (
            <>
              <ColumnsButton entityType="products" />
              <ExportMenu
                onExportExcel={exportProductsExcel}
                entityName="Products"
              />
              <button className="btn btn-secondary" onClick={handleExport}><Download size={18} /> Export CSV</button>
              {canDo('products', 'edit') && (
                <button className="btn btn-secondary" onClick={() => setShowImportModal(true)}><Upload size={18} /> Import CSV</button>
              )}
            </>
          )}
        </div>
      </div>

      {displayError && <div className="alert alert-error" role="alert" aria-live="assertive">{displayError}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input type="text" className="form-input" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search products" />
            </div>
            <select className="form-select filter-dropdown-sm" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} aria-label="Filter by status">
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            {(search || activeFilter) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setActiveFilter(''); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isMobile ? (
        <div className="mobile-card-list">
          {loading ? <CardSkeleton count={4} /> : products.length === 0 ? (
            search || activeFilter ? (
              <EmptyStateFiltered
                onClearFilters={() => { setSearch(''); setActiveFilter(''); }}
              />
            ) : (
              <EmptyState
                icon={Package}
                title="No products yet"
                description="Add your first product to build your catalog for quotes and orders."
                actionLabel={canDo('products', 'create') ? 'New Product' : undefined}
                onAction={canDo('products', 'create') ? () => addEditRow() : undefined}
                actionIcon={Plus}
              />
            )
          ) : products.map(product => (
            <MobileCard
              key={product.id}
              columns={visibleColumns}
              data={product}
              renderCell={renderReadOnlyCell}
              actions={<RowActions actions={getRowActions(product)} id={product.id} />}
              primaryField="name"
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Products</h3>
            {canDo('products', 'edit') && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {isEditing ? (
                  <>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={saveEditing}
                      disabled={saving}
                    >
                      <Check size={16} />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={startEditing}
                    title="Edit products"
                  >
                    <Edit size={16} />
                    Edit Products
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="table-container">
            <table className="table" aria-label="Products list">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortBy} sortDirection={sortOrder} onSort={isEditing ? undefined : handleSort} />
                  ))}
                  {isEditing && <th scope="col" style={{ width: '50px' }}></th>}
                  {!isEditing && <th scope="col" className="col-narrow"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={false} hasActions />
                ) : isEditing ? (
                  <>
                    {editRows.map((row, index) => (
                      <tr key={row.id || `new-${index}`}>
                        {visibleColumns.map(col => (
                          <td key={col.key}>{renderEditCell(col, row, index)}</td>
                        ))}
                        <td>
                          <button
                            className="btn-icon"
                            onClick={() => removeEditRow(index)}
                            title={row._isNew ? 'Remove' : 'Delete product'}
                            style={{ color: 'var(--accent-danger, #dc2626)' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={visibleColumns.length + 1} style={{ borderTop: 'none' }}>
                        <button className="btn btn-secondary btn-sm" onClick={addEditRow} type="button">
                          <Plus size={16} />
                          Add Product
                        </button>
                      </td>
                    </tr>
                  </>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      {search || activeFilter ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setActiveFilter(''); }}
                        />
                      ) : (
                        <EmptyState
                          icon={Package}
                          title="No products yet"
                          description="Add your first product to build your catalog for quotes and orders."
                          actionLabel={canDo('products', 'create') ? 'New Product' : undefined}
                          onAction={canDo('products', 'create') ? () => addEditRow() : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  products.map(product => (
                    <tr key={product.id}>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderReadOnlyCell(col, product)}</td>
                      ))}
                      <td className="col-actions">
                        <RowActions actions={getRowActions(product)} id={product.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && products.length > 0 && !isEditing && (
        <Pagination page={page} totalPages={totalPages} total={total} limit={limit}
          onPageChange={setPage} onLimitChange={setLimit} />
      )}

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)} title="Import Products" size="lg">
        <form onSubmit={handleImport}>
          <div className="modal-body">
            <p className="text-secondary mb-4">Paste CSV data with columns: name, sku, description, price, active</p>
            <div className="form-group">
              <label className="form-label">CSV Data *</label>
              <textarea className="form-textarea" style={{ minHeight: 200 }} value={importCsv} onChange={(e) => setImportCsv(e.target.value)} placeholder="name,sku,description,price,active&#10;Product 1,SKU001,Description,99.99,true" required />
            </div>
            <div className="form-group">
              <label className="checkbox-wrapper">
                <input type="checkbox" className="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
                Update existing products (match by SKU)
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Import</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDelete} title="Delete Product" message="Are you sure? Products used in quotes/orders cannot be deleted." confirmText="Delete" />
    </div>
  );
}

export default Products;
